import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '../components/ui/dropdown-menu';
import { FolderKanban, FileText, ArrowRight, Search, ArrowUpDown, ArrowUp, ArrowDown, MoreVertical, LayoutGrid, List } from 'lucide-react';
import { Link } from 'react-router-dom';
import { format } from '../utils/dateUtils';
import { Pagination } from '../components/Pagination';

import config from '../config';
const API = `${config.BACKEND_URL}/api`;

export const Projects = () => {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('name');
  const [sortOrder, setSortOrder] = useState('asc');
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const pageSize = 12;

  const fetchProjects = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        page: currentPage,
        page_size: pageSize
      };
      if (searchQuery) params.search = searchQuery;

      const response = await axios.get(`${API}/projects/paginated`, { params });
      setProjects(response.data.items);
      setTotalPages(response.data.total_pages);
      setTotalItems(response.data.total);
    } catch (error) {
      console.error('Failed to fetch projects:', error);
    } finally {
      setLoading(false);
    }
  }, [currentPage, pageSize, searchQuery]);

  useEffect(() => {
    fetchProjects();
  }, [fetchProjects]);

  const toggleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('asc');
    }
  };

  // Sort projects client-side
  const sortedProjects = [...projects].sort((a, b) => {
    let comparison = 0;
    
    if (sortBy === 'name') {
      comparison = a.name.localeCompare(b.name);
    } else if (sortBy === 'prefix') {
      comparison = a.prefix.localeCompare(b.prefix);
    } else if (sortBy === 'issues') {
      comparison = a.issue_counter - b.issue_counter;
    } else if (sortBy === 'created') {
      comparison = new Date(a.created_at) - new Date(b.created_at);
    }
    
    return sortOrder === 'asc' ? comparison : -comparison;
  });

  const getSortIcon = (field) => {
    if (sortBy !== field) return <ArrowUpDown className="h-4 w-4 text-muted-foreground" />;
    return sortOrder === 'asc' 
      ? <ArrowUp className="h-4 w-4 text-primary" /> 
      : <ArrowDown className="h-4 w-4 text-primary" />;
  };

  if (loading && projects.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner h-8 w-8" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="projects-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-semibold">Projects</h1>
          <p className="text-muted-foreground mt-1">View all projects in your organization.</p>
        </div>
      </div>

      {/* Search and Sort Controls */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search projects..."
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
              setCurrentPage(1);
            }}
            className="pl-9"
            data-testid="search-projects-input"
          />
        </div>
        <Select value={`${sortBy}-${sortOrder}`} onValueChange={(v) => {
          const [field, order] = v.split('-');
          setSortBy(field);
          setSortOrder(order);
        }}>
          <SelectTrigger className="w-48" data-testid="sort-projects-select">
            <SelectValue placeholder="Sort by..." />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="name-asc">Name (A-Z)</SelectItem>
            <SelectItem value="name-desc">Name (Z-A)</SelectItem>
            <SelectItem value="prefix-asc">Prefix (A-Z)</SelectItem>
            <SelectItem value="prefix-desc">Prefix (Z-A)</SelectItem>
            <SelectItem value="issues-desc">Most Issues</SelectItem>
            <SelectItem value="issues-asc">Least Issues</SelectItem>
            <SelectItem value="created-desc">Newest First</SelectItem>
            <SelectItem value="created-asc">Oldest First</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Sort buttons - alternative inline controls */}
      <div className="flex gap-2 flex-wrap">
        <Button 
          variant={sortBy === 'name' ? 'secondary' : 'outline'} 
          size="sm" 
          onClick={() => toggleSort('name')}
          className="gap-1"
        >
          Name {getSortIcon('name')}
        </Button>
        <Button 
          variant={sortBy === 'prefix' ? 'secondary' : 'outline'} 
          size="sm" 
          onClick={() => toggleSort('prefix')}
          className="gap-1"
        >
          Prefix {getSortIcon('prefix')}
        </Button>
        <Button 
          variant={sortBy === 'issues' ? 'secondary' : 'outline'} 
          size="sm" 
          onClick={() => toggleSort('issues')}
          className="gap-1"
        >
          Issues {getSortIcon('issues')}
        </Button>
        <Button 
          variant={sortBy === 'created' ? 'secondary' : 'outline'} 
          size="sm" 
          onClick={() => toggleSort('created')}
          className="gap-1"
        >
          Created {getSortIcon('created')}
        </Button>
      </div>

      {sortedProjects.length === 0 ? (
        <Card>
          <CardContent className="py-16">
            <div className="empty-state">
              <FolderKanban className="empty-state-icon" />
              <p className="empty-state-title">No projects found</p>
              <p className="empty-state-description">
                {searchQuery ? 'Try a different search term.' : 'Projects will appear here once created by an admin.'}
              </p>
            </div>
          </CardContent>
        </Card>
      ) : (
        <>
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {sortedProjects.map((project) => (
              <Card key={project.id} className="card-hover" data-testid={`project-card-${project.prefix}`}>
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <Badge variant="secondary" className="text-xs font-mono">
                      {project.prefix}
                    </Badge>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-8 w-8 -mr-2">
                          <MoreVertical className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem asChild>
                          <Link to={`/user-stories?project=${project.id}`} className="flex items-center gap-2">
                            <List className="h-4 w-4" />
                            View as List
                          </Link>
                        </DropdownMenuItem>
                        <DropdownMenuItem asChild>
                          <Link to={`/projects/${project.id}/kanban`} className="flex items-center gap-2">
                            <LayoutGrid className="h-4 w-4" />
                            View Kanban
                          </Link>
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                  <CardTitle className="text-lg mt-2">{project.name}</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center justify-between text-sm text-muted-foreground">
                    <span className="flex items-center gap-1">
                      <FileText className="h-4 w-4" />
                      {project.issue_counter} issues
                    </span>
                    <div className="flex gap-1">
                      <Link to={`/projects/${project.id}/kanban`}>
                        <Button variant="ghost" size="sm" className="gap-1 h-8" title="Kanban View">
                          <LayoutGrid className="h-4 w-4" />
                        </Button>
                      </Link>
                      <Link to={`/user-stories?project=${project.id}`}>
                        <Button variant="ghost" size="sm" className="gap-1 h-8">
                          View <ArrowRight className="h-3 w-3" />
                        </Button>
                      </Link>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
          
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            totalItems={totalItems}
            pageSize={pageSize}
            onPageChange={setCurrentPage}
          />
        </>
      )}
    </div>
  );
};
